import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useRefreshFiles } from "@/hooks/use-knowledge-base";
import {
  failureMessage,
  getFileText,
  type KnowledgeBaseFile,
  type KnowledgeBaseUsage,
  sha256Hex,
  TEXT_NOT_LOADED,
  uploadFile,
} from "@/services/knowledge-base.service";
import { UPLOAD_FAILED } from "@/lib/resumable-upload";
import {
  type FaqEntry,
  faqError,
  faqMarkdown,
  filledEntries,
  parseFaq,
  textError,
  textFileName,
  titleOf,
  TITLE_MAX_CHARS,
} from "@/utils/kb-text";
import { checkFiles, uploadRoom } from "@/utils/kb-upload-rules";

type Kind = "text" | "faq";

const EMPTY_ENTRY: FaqEntry = { question: "", answer: "" };

interface TextDialogProps {
  storeId: string;
  workspaceId: string;
  /** The knowledge base's files now. */
  files: KnowledgeBaseFile[];
  /** The plan's limits and what is used of them; undefined while they load. */
  usage: KnowledgeBaseUsage | undefined;
  /** Edit: the ready text file this one replaces, opened with its text. */
  editing?: KnowledgeBaseFile;
  onClose: () => void;
}

/**
 * Add text, or a list of questions and answers, to a knowledge base (owner, 2026-10-21: each is a document of its
 * own, kb-text.ts). Saved as a .md file through the upload path, so the plan's limits are a file's and are checked
 * here first, as the upload dialog checks them; Edit opens a ready text file again and saves the new text in its
 * place (Replace: the old one is searched until the new one is ready). Nothing is sent while the form is invalid.
 */
export function TextDialog({ storeId, workspaceId, files, usage, editing, onClose }: TextDialogProps) {
  const [kind, setKind] = useState<Kind>("text");
  const [title, setTitle] = useState(editing ? titleOf(editing.display_name) : "");
  const [body, setBody] = useState("");
  const [entries, setEntries] = useState<FaqEntry[]>([EMPTY_ENTRY]);
  /** Edit's text, while it is read: null until it has loaded, a sentence if it could not be. */
  const [loading, setLoading] = useState<string | null>(editing ? null : "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const refresh = useRefreshFiles(storeId, workspaceId);
  const { toast } = useToast();

  useEffect(() => {
    if (!editing) return;
    getFileText(editing.id).then(
      (text) => {
        const faq = parseFaq(text);
        if (faq) {
          setKind("faq");
          setEntries(faq);
        } else {
          setBody(text);
        }
        setLoading("");
      },
      () => setLoading(TEXT_NOT_LOADED),
    );
  }, [editing]);

  const setEntry = (index: number, change: Partial<FaqEntry>) =>
    setEntries((all) => all.map((entry, i) => (i === index ? { ...entry, ...change } : entry)));

  const save = async () => {
    const invalid = kind === "text" ? textError(title, body) : faqError(title, entries);
    if (invalid) return setError(invalid);
    const text = kind === "text" ? `${body.trim()}\n` : faqMarkdown(filledEntries(entries));
    const file = new File([text], textFileName(title), { type: "text/markdown" });
    setSaving(true);
    try {
      const hash = await sha256Hex(file);
      const refused = usage && checkFiles([{ name: file.name, size: file.size, hash }], uploadRoom(usage, files, editing), files)[0];
      if (refused) {
        setError(refused);
        return;
      }
      await uploadFile(storeId, file, hash, () => undefined, editing?.id);
      await refresh();
      toast({ title: editing ? "Text updated" : "Text added", description: `"${file.name}" is being processed.` });
      onClose();
    } catch (failure) {
      setError(failureMessage(failure, UPLOAD_FAILED));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => !saving && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.display_name}` : "Add text"}</DialogTitle>
          <DialogDescription>
            {editing
              ? `The new text is used once it is ready. Until then, your agents keep using the text as it was.`
              : "Write or paste what your agents should know, or a list of questions and answers. It is saved as a file of this knowledge base."}
          </DialogDescription>
        </DialogHeader>

        {loading === null ? (
          <div role="status" aria-label="Loading the text" className="space-y-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : loading ? (
          <p role="alert" className="text-sm text-destructive">
            {loading}
          </p>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            {!editing && (
              <Tabs value={kind} onValueChange={(next) => setKind(next as Kind)}>
                <TabsList>
                  <TabsTrigger value="text">Text</TabsTrigger>
                  <TabsTrigger value="faq">Questions and answers</TabsTrigger>
                </TabsList>
              </Tabs>
            )}

            <div className="space-y-1">
              <Label htmlFor="kb-text-title">Name</Label>
              <Input
                id="kb-text-title"
                value={title}
                maxLength={TITLE_MAX_CHARS}
                placeholder={kind === "text" ? "Opening hours" : "Common questions"}
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>

            {kind === "text" ? (
              <div className="space-y-1">
                <Label htmlFor="kb-text-body">Text</Label>
                <Textarea
                  id="kb-text-body"
                  rows={10}
                  value={body}
                  placeholder="Short sections under clear headings answer best."
                  onChange={(event) => setBody(event.target.value)}
                />
              </div>
            ) : (
              <ol aria-label="Questions and answers" className="max-h-[45vh] space-y-3 overflow-y-auto">
                {entries.map((entry, index) => (
                  <li key={index} data-testid="kb-faq-entry" className="space-y-2 rounded-md border p-3">
                    <div className="flex items-center gap-2">
                      <Input
                        aria-label={`Question ${index + 1}`}
                        value={entry.question}
                        placeholder="Do you take walk-ins?"
                        onChange={(event) => setEntry(index, { question: event.target.value })}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Remove question ${index + 1}`}
                        disabled={entries.length === 1}
                        onClick={() => setEntries((all) => all.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <Textarea
                      aria-label={`Answer ${index + 1}`}
                      rows={2}
                      value={entry.answer}
                      placeholder="Yes, from 10 to 6. Booked visits go first."
                      onChange={(event) => setEntry(index, { answer: event.target.value })}
                    />
                  </li>
                ))}
              </ol>
            )}
            {kind === "faq" && (
              <Button type="button" variant="outline" size="sm" onClick={() => setEntries((all) => [...all, EMPTY_ENTRY])}>
                <Plus className="mr-2 h-4 w-4" />
                Add a question
              </Button>
            )}

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} aria-busy={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
